import ScrollModal from "@/components/common/scroll-modal";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Badge, Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import VersionCard from "./VersionCard";
import type { ProcessTypeResponse } from "@/types/entities/process";
import {
  useGetMainFlowsByProcessId,
  //usePublishFlow,
  useUpdateFlowStatus,
  useArchiveFlow,
} from "@/queries/flows/flows.api";
import { Renderify } from "renderify-js";
import type { ModalStateType } from "./Detail";
import { useQueryClient } from "@tanstack/react-query";

interface VersionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  process?: ProcessTypeResponse;
  setModalType?: (type: ModalStateType) => void;
  onSelectVersion?: (version: number) => void;
  isVersionPending?: boolean;
  pendingVersion?: number | null;
  onRestore?: (flowId: string) => void;
}

export const VersionModal = ({
  isOpen = false,
  onClose = () => {},
  process,
  setModalType = () => {},
  onSelectVersion,
  isVersionPending = false,
  pendingVersion,
  onRestore,
}: VersionModalProps) => {
  const { hasPermission } = useAuthorization();
  const queryClient = useQueryClient();
  //const canEdit = hasPermission(Permissions.flows.update);
  const canCreateVersion = hasPermission(Permissions.flows["create-version"]);
  const canPerformReview = hasPermission(Permissions.flows["perform-review"]);
  const { showToast } = useToast();

  const { data: mainFlows, isLoading } = useGetMainFlowsByProcessId(
    process?.data?.id,
  );

  // const { mutateAsync: publishFlow, isPending: isPublishing } =
  //   usePublishFlow();
  const { mutateAsync: updateFlowStatus, isPending: isPublishing } =
    useUpdateFlowStatus();

  const { mutateAsync: archiveFlow, isPending: isArchiving } = useArchiveFlow();

  const versions = mainFlows?.data || [];

  const getConsolidatedStatus = (flow: (typeof versions)[0]) => {
    if (flow.status === "PUBLISHED") {
      return "ACTIVE";
    }
    if (flow.review_status === "DENIED") {
      return "DENIED";
    }
    return null;
  };

  // Verificar si una versión puede ser publicada (debe tener revisión aprobada)
  const canPublishVersion = (flow: (typeof versions)[0]) => {
    const hasApprovedRevision = flow.revisions?.some(
      (r) => r.status === "APPROVED",
    );
    return hasApprovedRevision && flow.status !== "PUBLISHED";
  };

  const handlePublish = async (flowId: string) => {
    const flow = versions.find((f) => f.id === flowId);

    if (!flow || !canPublishVersion(flow)) {
      showToast({
        title: "No se puede publicar",
        description:
          "Solo se pueden publicar versiones con veredicto aprobado.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      return;
    }

    try {
      // await publishFlow(flowId);
      await updateFlowStatus({ id: flowId, status: "PUBLISHED" });
      await queryClient.refetchQueries({ queryKey: ["main-flows"] });
      await queryClient.refetchQueries({ queryKey: ["flows"] });
      await queryClient.refetchQueries({
        queryKey: ["admin-process-detail", process?.data?.id],
      });
      showToast({
        title: "Versión publicada",
        description: "La versión ha sido publicada correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error publishing version:", error);
      showToast({
        title: "Error al publicar",
        description: "Ha ocurrido un error al intentar publicar la versión.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const handleArchive = async (flowId: string) => {
    try {
      await archiveFlow(flowId);
      showToast({
        title: "Versión archivada",
        description: "La versión ha sido archivada correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error archiving version:", error);
      showToast({
        title: "Error al archivar",
        description: "Ha ocurrido un error al intentar archivar la versión.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3 border-b border-neutral-silent">
          <DialogTitle>Versiones</DialogTitle>
          <DialogDescription className="sr-only">
            Versionado del proceso
          </DialogDescription>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        canCreateVersion && (
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 border-t border-neutral-silent py-3 px-8 gap-3">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={onClose}
              className="min-w-30 cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              size="sm"
              type="button"
              disabled={isVersionPending || isPublishing || isArchiving}
              onClick={() => setModalType("new-version")}
              className="min-w-30 cursor-pointer"
            >
              {isVersionPending ? "Creando versión..." : "Crear nueva versión"}
            </Button>
          </div>
        )
      }
    >
      <div className="px-8 py-4">
        {/* Mostrar versión pendiente primero */}
        {isVersionPending && pendingVersion && (
          <div className="border border-neutral rounded-lg p-4 flex flex-col gap-3 mb-4 [box-shadow:0px_1px_2px_rgba(17,20,24,0.08)] opacity-70">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <h3 className="font-normal text-body-loud">
                  Versión {pendingVersion}
                </h3>
                <p className="text-sm text-body-quiet flex items-center gap-2">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin"
                  />
                  Creando versión...
                </p>
              </div>
              <Badge variant="neutral" size="sm">
                Procesando
              </Badge>
            </div>
          </div>
        )}
        <Renderify
          condition={isLoading}
          elseShow={
            <Renderify
              condition={versions.length === 0 && !isVersionPending}
              elseShow={
                <div className="flex flex-col gap-3">
                  {versions.map((flow) => {
                    const originFlow = flow.duplicated_origin_id
                      ? versions.find((v) => v.id === flow.duplicated_origin_id)
                      : null;
                    const isRestored =
                      originFlow &&
                      originFlow.version_number !==
                        (flow.version_number || 1) - 1;
                    return (
                      <div
                        key={flow.id}
                        onClick={() => {
                          if (onSelectVersion && flow.version_number) {
                            onSelectVersion(flow.version_number);
                            onClose();
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <VersionCard
                          flowId={flow.id}
                          versionNumber={flow.version_number}
                          comment={flow.version_description || ""}
                          createdAt={flow.created_at}
                          createdBy={flow.created_by}
                          status={getConsolidatedStatus(flow)}
                          //canPublish={canPublishVersion(flow)}
                          canPublish={
                            canPublishVersion(flow) && canPerformReview
                          }
                          canRestore={canCreateVersion}
                          isRestored={!!isRestored}
                          restoredFromVersion={originFlow?.version_number}
                          onRestore={(id) => {
                            onRestore?.(id);
                          }}
                          onPublish={handlePublish}
                          onArchive={handleArchive}
                        />
                      </div>
                    );
                  })}
                </div>
              }
            >
              <p className="text-center text-body-quiet">
                No hay versiones disponibles para este proceso.
              </p>
            </Renderify>
          }
        >
          <div className="flex justify-center items-center">
            <Icon
              type="far"
              name="spinner-third"
              className="text-body-quiet text-3xl animate-spin"
            />
          </div>
        </Renderify>
      </div>
    </ScrollModal>
  );
};
